// web/src/copilot/api.ts
//
// Thin fetch wrappers for copilot-specific endpoints: /agent/usage and
// /agent/conversations. Uses apiFetch from src/api/client so the Supabase JWT,
// org header, and 401-refresh logic are applied automatically.
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";

// ---------------------------------------------------------------------------
// Usage
// ---------------------------------------------------------------------------

export interface UsageData {
  user_id: string;
  tier: "free" | "pro" | "analyst";
  day: string;
  messages_used: number;
  messages_limit: number;
  messages_remaining: number;
  cost_usd_used: number;
  cost_usd_safety_cap: number;
  rate_limit_state:
    | "ok"
    | "warning"
    | "messages_exhausted"
    | "cost_safety_cap"
    | "tier_required";
}

// Query keys exported so other components can invalidate after mutations
// the backend can't otherwise notify us about (e.g. CopilotKit message-end).
export const USAGE_QUERY_KEY = ["agent", "usage"] as const;
export const CONVERSATIONS_QUERY_KEY = ["agent", "conversations"] as const;

export function useUsage() {
  return useQuery<UsageData>({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => apiFetch<UsageData>("/api/v1/agent/usage"),
    // Background poll as a safety net. The chat sheet also explicitly
    // invalidates this query when CopilotKit transitions out of isLoading,
    // which gives a snappy update right after each completed turn.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  id: string;
  title: string | null;
  updated_at: string;
  message_count: number;
}

export function useConversations() {
  return useQuery<{ conversations: ConversationSummary[] }>({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: () =>
      apiFetch<{ conversations: ConversationSummary[] }>(
        "/api/v1/agent/conversations",
      ),
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Conversation messages (for history hydration)
// ---------------------------------------------------------------------------

export interface ConversationMessage {
  id: number | string;
  conversation_id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ConversationMessagesResponse {
  conversation_id: string;
  messages: ConversationMessage[];
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery<ConversationMessagesResponse>({
    queryKey: ["agent", "conversation", conversationId, "messages"],
    queryFn: () =>
      apiFetch<ConversationMessagesResponse>(
        `/api/v1/agent/conversations/${encodeURIComponent(conversationId!)}/messages`,
      ),
    enabled: conversationId !== null,
    staleTime: Infinity, // history doesn't change
  });
}
